// Keep the original red/green ramp and brightness. Once it reaches saturated
// yellow, use the unused blue channel to reveal detail as lemon and pale yellow.
const YELLOW_START = 255 / 7;
const SHALLOW_LIMIT = 255;
const SHALLOW_BLUE = 225;
const MAX_ESCAPE_COUNT = 20000;
const HIGHLIGHT_RANGE = Math.log(SHALLOW_LIMIT / YELLOW_START);
const DEEP_RANGE = Math.log(MAX_ESCAPE_COUNT / SHALLOW_LIMIT);
const INV_HIGHLIGHT_RANGE = 1 / HIGHLIGHT_RANGE;
const INV_DEEP_RANGE = 1 / DEEP_RANGE;
const INV_YELLOW_START = 1 / YELLOW_START;
const INV_SHALLOW_LIMIT = 1 / SHALLOW_LIMIT;
const MU_BUCKETS = 16384;
const SHALLOW_BUCKETS = 2048;
const SHALLOW_SPAN = SHALLOW_LIMIT - YELLOW_START;
const INV_SHALLOW_SPAN = SHALLOW_BUCKETS / SHALLOW_SPAN;
export const WARM_COLORS = [
  [15, 10, 8], [75, 18, 3], [180, 45, 2],
  [250, 115, 8], [255, 220, 90], [255, 250, 210],
];
let muBaseTable = null;
let muSlopeTable = null;
let blueThresholds = null;
let shallowBucketLo = null;
let shallowBucketHi = null;
let initializingTables = false;

/** Expand clipped yellow highlights without dimming the original fire colors. */
export function writeFireColor(pixels, offset, value) {
  value = Math.max(0, value);
  // Match the original translucent low-count colors over the black background,
  // but keep the result opaque for the deep renderer's linear-light averaging.
  if (value <= YELLOW_START) {
    const opacity = Math.min(1, value * 15 / 255);
    pixels[offset] = Math.round(255 * opacity);
    pixels[offset + 1] = Math.round(Math.min(255, value * 7) * opacity);
    pixels[offset + 2] = 0;
    pixels[offset + 3] = 255;
    return;
  }
  if (!initializingTables) {
    if (!blueThresholds) initFireFastTables();
    if (value < SHALLOW_LIMIT) {
      const k = ((value - YELLOW_START) * INV_SHALLOW_SPAN) | 0;
      let lo = shallowBucketLo[k];
      const hi = shallowBucketHi[k];
      while (lo < hi && value >= blueThresholds[lo]) lo++;
      const prevT = lo > 0 ? blueThresholds[lo - 1] : YELLOW_START;
      const nextT = blueThresholds[lo];
      if (value - 1e-11 >= prevT && value + 1e-11 < nextT) {
        pixels[offset] = 255;
        pixels[offset + 1] = 255;
        pixels[offset + 2] = lo;
        pixels[offset + 3] = 255;
        return;
      }
    } else if (value <= MAX_ESCAPE_COUNT) {
      let lo = 225;
      while (lo < 255 && value >= blueThresholds[lo]) lo++;
      const prevT = lo > 225 ? blueThresholds[lo - 1] : SHALLOW_LIMIT;
      const nextT = blueThresholds[lo];
      if (value - 1e-9 >= prevT && value + 1e-9 < nextT) {
        pixels[offset] = 255;
        pixels[offset + 1] = 255;
        pixels[offset + 2] = lo;
        pixels[offset + 3] = 255;
        return;
      }
    }
  }
  let highlight = 1;
  let deepHighlight = 0;
  if (value < SHALLOW_LIMIT) {
    highlight = Math.sqrt(Math.min(1, Math.log(value * INV_YELLOW_START) * INV_HIGHLIGHT_RANGE));
  } else {
    deepHighlight = Math.min(1, Math.log(value * INV_SHALLOW_LIMIT) * INV_DEEP_RANGE);
  }
  pixels[offset] = 255;
  pixels[offset + 1] = 255;
  pixels[offset + 2] = Math.round(SHALLOW_BLUE * highlight + (255 - SHALLOW_BLUE) * deepHighlight);
  pixels[offset + 3] = 255;
}

function initFireFastTables() {
  initializingTables = true;
  getMuLookupTables();
  const probe = new Uint8ClampedArray(4);
  blueThresholds = new Float64Array(256);
  for (let b = 0; b < 255; b++) {
    let lo = YELLOW_START;
    let hi = MAX_ESCAPE_COUNT;
    for (let it = 0; it < 60; it++) {
      const mid = 0.5 * (lo + hi);
      writeFireColor(probe, 0, mid);
      if (probe[2] <= b) lo = mid;
      else hi = mid;
    }
    blueThresholds[b] = hi;
  }
  blueThresholds[255] = Infinity;
  shallowBucketLo = new Uint8Array(SHALLOW_BUCKETS);
  shallowBucketHi = new Uint8Array(SHALLOW_BUCKETS);
  let curB = 0;
  for (let k = 0; k < SHALLOW_BUCKETS; k++) {
    const v0 = YELLOW_START + (k / SHALLOW_BUCKETS) * SHALLOW_SPAN;
    const v1 = YELLOW_START + ((k + 1) / SHALLOW_BUCKETS) * SHALLOW_SPAN;
    while (curB < 255 && v0 >= blueThresholds[curB]) curB++;
    shallowBucketLo[k] = curB;
    let endB = curB;
    while (endB < 255 && v1 >= blueThresholds[endB]) endB++;
    shallowBucketHi[k] = endB;
  }
  initializingTables = false;
}

/** Shared 16,384-bucket linear interpolation table for 1 - log2(0.5 * log(d2)) on [4, 260). */
export function getMuLookupTables() {
  if (!muBaseTable) {
    muBaseTable = new Float64Array(MU_BUCKETS);
    muSlopeTable = new Float64Array(MU_BUCKETS);
    let prevMu = 1 - Math.log2(0.5 * Math.log(4));
    for (let b = 0; b < MU_BUCKETS; b++) {
      const nextMu = 1 - Math.log2(0.5 * Math.log(4 + (b + 1) / 64));
      muBaseTable[b] = prevMu;
      muSlopeTable[b] = nextMu - prevMu;
      prevMu = nextMu;
    }
  }
  return { muBaseTable, muSlopeTable };
}

function resolveFastFireColor32(it, d2) {
  if (!(d2 >= 4 && d2 < 260)) return 0;
  if (!blueThresholds) initFireFastTables();
  const u = (d2 - 4) * 64;
  const b = u | 0;
  const vApprox = it + muBaseTable[b] + muSlopeTable[b] * (u - b);
  if (vApprox + 3.6e-6 <= YELLOW_START) {
    if (vApprox >= 17 + 3.6e-6) {
      const gFloat = vApprox * 7;
      const g0 = (gFloat + (0.5 - 3e-5)) | 0;
      if (g0 === ((gFloat + (0.5 + 3e-5)) | 0)) {
        return (0xff0000ff | ((g0 > 255 ? 255 : g0) << 8)) >>> 0;
      }
    } else if (vApprox > 3.6e-6 && vApprox + 3.6e-6 < 17) {
      const rFloat = vApprox * 15;
      const r0 = (rFloat + (0.5 - 6e-5)) | 0;
      if (r0 === ((rFloat + (0.5 + 6e-5)) | 0)) {
        const gFloat = vApprox * vApprox * (105 / 255);
        const g0 = (gFloat + (0.5 - 5e-5)) | 0;
        if (g0 === ((gFloat + (0.5 + 5e-5)) | 0)) {
          return (0xff000000 | (g0 << 8) | r0) >>> 0;
        }
      }
    }
  } else if (vApprox - 3.6e-6 > YELLOW_START && vApprox + 3.6e-6 < SHALLOW_LIMIT) {
    const k = ((vApprox - YELLOW_START) * INV_SHALLOW_SPAN) | 0;
    let lo = shallowBucketLo[k];
    const hi = shallowBucketHi[k];
    while (lo < hi && vApprox >= blueThresholds[lo]) lo++;
    const prevT = lo > 0 ? blueThresholds[lo - 1] : YELLOW_START;
    const nextT = blueThresholds[lo];
    if (vApprox - 3.6e-6 >= prevT && vApprox + 3.6e-6 < nextT) {
      return (0xff00ffff | (lo << 16)) >>> 0;
    }
  } else if (vApprox - 3.6e-6 >= SHALLOW_LIMIT) {
    let lo = 225;
    while (lo < 255 && vApprox >= blueThresholds[lo]) lo++;
    const prevT = lo > 225 ? blueThresholds[lo - 1] : SHALLOW_LIMIT;
    const nextT = blueThresholds[lo];
    if (vApprox - 3.6e-6 >= prevT && vApprox + 3.6e-6 < nextT) {
      return (0xff00ffff | (lo << 16)) >>> 0;
    }
  }
  return 0;
}

/** Fast bit-exact 32-bit RGBA fire color writer for smooth (mu) escape counts. */
export function writeFirePixelMuFast(pixels, pixels32, pixelIdx, it, d2, fallbackMu) {
  const color32 = resolveFastFireColor32(it, d2);
  if (color32 !== 0) {
    pixels32[pixelIdx] = color32;
    return;
  }
  writeFireColor(pixels, pixelIdx * 4, fallbackMu(it, d2));
}

/** Fast bit-exact 4-byte RGBA fire color writer for deepZoomColor writePixel. */
export function writeFireColorMuFast(pixels, offset, it, d2) {
  const color32 = resolveFastFireColor32(it, d2);
  if (color32 !== 0) {
    pixels[offset] = color32 & 0xff;
    pixels[offset + 1] = (color32 >>> 8) & 0xff;
    pixels[offset + 2] = (color32 >>> 16) & 0xff;
    pixels[offset + 3] = 255;
    return;
  }
  const value = d2 > 1 && d2 < Infinity ? it + 1 - Math.log2(0.5 * Math.log(d2)) : it;
  writeFireColor(pixels, offset, value);
}

/** Map a smooth escape count onto the deep-zoom warm fire ramp over [r0, r1]. */
export function writeWarmRangeColor(pixels, offset, value, range) {
  const r0 = range[0];
  const r1 = range[1];
  if (value <= r0) {
    pixels[offset] = 15;
    pixels[offset + 1] = 10;
    pixels[offset + 2] = 8;
    pixels[offset + 3] = 255;
    return;
  }
  if (value >= r1) {
    pixels[offset] = 255;
    pixels[offset + 1] = 250;
    pixels[offset + 2] = 210;
    pixels[offset + 3] = 255;
    return;
  }
  const exposure = (value - r0) / (r1 - r0);
  const position = exposure * (WARM_COLORS.length - 1);
  const index = position >= WARM_COLORS.length - 2 ? WARM_COLORS.length - 2 : position | 0;
  const blend = position - index;
  const a = WARM_COLORS[index];
  const b = WARM_COLORS[index + 1];
  pixels[offset] = a[0] + (b[0] - a[0]) * blend;
  pixels[offset + 1] = a[1] + (b[1] - a[1]) * blend;
  pixels[offset + 2] = a[2] + (b[2] - a[2]) * blend;
  pixels[offset + 3] = 255;
}

// Canvas callbacks consume the result immediately; reuse this array per pixel.
const color = [0, 0, 0, 255];
export function fireColor({ mu }) {
  writeFireColor(color, 0, mu);
  return color;
}
// Explicit metadata survives minification and avoids parsing a nonlinear function.
fireColor.colorParams = Object.freeze({ palette: 'fire', useMu: true });

// Shader constants come from the same exposure values as the CPU renderer.
export const FIRE_PALETTE_GLSL = `
  vec3 fireColor(float value) {
    value = max(0.0, value);
    float opacity = min(1.0, value * 15.0 / 255.0);
    float highlight = sqrt(min(1.0, log(max(1.0, value / ${YELLOW_START.toFixed(12)})) / ${HIGHLIGHT_RANGE.toFixed(12)}));
    float deepHighlight = min(1.0, log(max(1.0, value / ${SHALLOW_LIMIT.toFixed(1)})) / ${DEEP_RANGE.toFixed(12)});
    vec3 color = vec3(255.0 * opacity, min(255.0, value * 7.0) * opacity,
      ${SHALLOW_BLUE.toFixed(1)} * highlight + ${(255 - SHALLOW_BLUE).toFixed(1)} * deepHighlight);
    return floor(color + 0.5) / 255.0;
  }

  vec3 warmRangeColor(float value, vec2 range) {
    float t = clamp((value - range.x) / (range.y - range.x), 0.0, 1.0);
    float pos = t * 5.0;
    vec3 c0;
    vec3 c1;
    float blend;
    if (pos < 1.0) {
      c0 = vec3(15.0, 10.0, 8.0);
      c1 = vec3(75.0, 18.0, 3.0);
      blend = pos;
    } else if (pos < 2.0) {
      c0 = vec3(75.0, 18.0, 3.0);
      c1 = vec3(180.0, 45.0, 2.0);
      blend = pos - 1.0;
    } else if (pos < 3.0) {
      c0 = vec3(180.0, 45.0, 2.0);
      c1 = vec3(250.0, 115.0, 8.0);
      blend = pos - 2.0;
    } else if (pos < 4.0) {
      c0 = vec3(250.0, 115.0, 8.0);
      c1 = vec3(255.0, 220.0, 90.0);
      blend = pos - 3.0;
    } else {
      c0 = vec3(255.0, 220.0, 90.0);
      c1 = vec3(255.0, 250.0, 210.0);
      blend = pos - 4.0;
    }
    return floor(mix(c0, c1, blend) + 0.5) / 255.0;
  }
`;
